import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { WireConfirmationPayloadType } from '@/api/v1/organization/vendor-vault/sendWireConfirmationApi';
import { OptionGrid } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { FormikDatePicker } from '@/components/form/fields/FormikDatePicker/FormikDatePicker';
import { formatPayloadDate } from '@/helpers/dateHelpers/dateHelpers';

type WireConfirmationFormType = {
  defaultSendTo: string;
  isLoading: boolean;
  onSubmit: (payload: WireConfirmationPayloadType) => void;
};

type FormValuesType = Omit<WireConfirmationPayloadType, 'date'> & { date: Date | null };

export const WireConfirmationForm = ({ defaultSendTo, isLoading, onSubmit }: WireConfirmationFormType) => {
  const formik = useFormik<FormValuesType>({
    initialValues: { reference: '', amount: '', date: null, send_to: defaultSendTo, note: '' },
    validationSchema: Yup.object({
      reference: Yup.string().trim().required('Enter the confirmation or tracking number.').max(255),
      amount: Yup.string().trim().max(64, 'Keep the amount under 64 characters.'),
      date: Yup.date().nullable().max(new Date(), 'The date cannot be in the future.'),
      send_to: Yup.string().trim().required('Enter where to send it.'),
    }),
    onSubmit: (values) =>
      onSubmit({
        ...values,
        reference: values.reference.trim(),
        amount: values.amount.trim(),
        date: values.date ? formatPayloadDate(values.date) : null,
        send_to: values.send_to.trim(),
      }),
  });

  return (
    <FormikForm value={formik}>
      <OptionGrid $cols={2} $gap='0 20px'>
        <div style={{ gridColumn: 'span 2' }}>
          <FormikInput
            name='reference'
            label='Wire confirmation or tracking number*'
            placeholder='e.g. IMAD 20261013 ...'
            autoComplete='new-field'
          />
        </div>
        <FormikInput name='amount' label='Amount' placeholder='SEK 48,200.00' autoComplete='new-field' />
        <FormikDatePicker name='date' label='Date sent' placeholder='Pick a date' maxDate={new Date()} isClearable />
        <div style={{ gridColumn: 'span 2' }}>
          <FormikInput name='send_to' label='Send to*' placeholder='email or mobile' autoComplete='new-field' />
        </div>
      </OptionGrid>
      <Box display='flex' justifyContent='flex-end' marginTop='12px'>
        <ButtonNew size='medium' isLoading={isLoading} onClick={() => formik.handleSubmit()}>
          Send confirmation
        </ButtonNew>
      </Box>
    </FormikForm>
  );
};
